import axios from 'axios';
import { SYSTEMDOX_API_URL } from '@/lib/apiConfig';

const api = axios.create({ baseURL: SYSTEMDOX_API_URL });

export interface CheckOutput {
  title: string;
  summary: string;
  text: string;
}

export interface CheckResult {
  name: string;
  status: 'passed' | 'failed' | 'skipped';
  conclusion: string;
  scope: string;
  headSha: string;
  detailsUrl: string;
  completedAt: string;
  durationMs: number;
  output: CheckOutput;
  guardrailId: string;
  guardrailTitle: string;
}

export interface ResultSummary {
  total: number;
  passed: number;
  failed: number;
  skipped: number;
}

export interface FitnessReport {
  repo: string;
  timestamp: string;
  commit: string;
  checks: CheckResult[];
  summary: ResultSummary;
}

export interface RunReport {
  service: string;
  repo: string;
  workflow: string;
  conclusion: string;
  status: string;
  runUrl: string;
  timestamp: string;
  commit: string;
  results: { name: string; status: 'passed' | 'failed' | 'skipped'; duration_ms: number }[];
  summary: ResultSummary;
}

export interface GuardrailMeta {
  id: string;
  title: string;
  category: string;
  severity: string;
  fitnessTestName: string;
}

export interface FitnessResults {
  reports: FitnessReport[];
  runs: RunReport[];
  guardrails: GuardrailMeta[];
}

/**
 * A CI failure, carried from the cell the user clicked into the analysis prompt.
 * The point of the loop: the model reads what the check actually printed on the
 * actual commit, instead of the user retyping it from memory.
 */
export interface CiFailure {
  checkName: string;
  repo?: string;
  commit?: string;
  conclusion?: string;
  detailsUrl?: string;
  output?: string;
  diff?: string;
  guardrailId?: string;
  guardrailTitle?: string;
}

export function toCiFailure(repo: string, check: CheckResult): CiFailure {
  const output = [check.output?.title, check.output?.summary, check.output?.text]
    .filter(Boolean)
    .join('\n');
  return {
    checkName: check.name,
    repo,
    commit: check.headSha,
    conclusion: check.conclusion,
    detailsUrl: check.detailsUrl,
    output,
    guardrailId: check.guardrailId,
    guardrailTitle: check.guardrailTitle,
  };
}

/** Human-readable prose for the textarea the failure is dropped into. */
export function describeFailures(failures: CiFailure[]): string {
  if (failures.length === 0) return '';
  const lines = [
    failures.length === 1
      ? 'This architecture fitness check failed in CI:'
      : `These ${failures.length} architecture fitness checks failed in CI:`,
    '',
  ];
  for (const f of failures) {
    lines.push(`- ${f.checkName}${f.repo ? ` — ${f.repo}` : ''}${f.commit ? ` @ ${f.commit.slice(0, 7)}` : ''}`);
    if (f.output) {
      lines.push(...f.output.split('\n').map((l) => `    ${l}`));
    }
  }
  lines.push('', 'The full check output is attached to this analysis.');
  return lines.join('\n');
}

export const fitnessApi = {
  setAuthProvider(getToken: () => Promise<string | null>) {
    api.interceptors.request.use(async (config) => {
      const token = await getToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      return config;
    });
  },

  async getResults(): Promise<FitnessResults> {
    const { data } = await api.get('/fitness/results');
    return data;
  },
};
